import * as React from 'react';

import Dropdown from 'af-components/Controls/Dropdown';

import styles from './styles.module.scss';

interface AdditionalFilterOption {
	id: number | null | string;
	label: string;
}

interface Props {
	options: AdditionalFilterOption[] | null;
	onChange: (options: AdditionalFilterOption | null) => void;
	selected: Nullable<AdditionalFilterOption>;
	defaultValue?: number;
	/** Define how to call the filtering option will be referred to */
	filterName: string;
	onRemoveFilter?: () => void;
	/** Outer apperance of dropdown needs to be different if it's for bulk modal */
	forBulkModal: boolean;
	/** Should the dropdown menu be opened immediately? */
	autoOpen: boolean;
	/** Enabling this adds "None" as the first option on the list. Will send "null" as requested value of the filter */
	hasNoneAsOption?: true;
	/** Define how you'd like to display a single option within the multiselect dropdown */
	renderOptionOverride?: (option: AdditionalFilterOption) => React.JSX.Element;
}

const Singleselect: React.FC<Props> = (props) => {
	const {
		options,
		onChange,
		defaultValue,
		selected,
		filterName,
		onRemoveFilter,
		forBulkModal,
		autoOpen,
		hasNoneAsOption,
		renderOptionOverride,
	} = props;

	const allOptions = React.useMemo(() => {
		if (options === null) {
			// we want to make sure we're displaying loading indicator
			return null;
		} else if (hasNoneAsOption) {
			const noneAsOption: AdditionalFilterOption = { id: null, label: 'N/A' };
			return [noneAsOption, ...options];
		} else {
			return options;
		}
	}, [options, hasNoneAsOption]);

	const renderOption = React.useCallback((option: AdditionalFilterOption) => {
		return (
			<div className={styles.dropdownOption}>
				{
					!!renderOptionOverride
						? renderOptionOverride(option)
						: option.label
				}
			</div>
		);
	}, [renderOptionOverride]);

	const onValueChange = React.useCallback((_option: AdditionalFilterOption) => {
		onChange(_option);
	}, [onChange]);

	const defaultOption = React.useMemo(() => {
		if (allOptions === null) {
			return null;
		}

		const _option = allOptions.find((option) => option.id === defaultValue);
		return _option ?? allOptions[0];
	}, [defaultValue, allOptions]);

	const placeholder = React.useMemo(() => {
		const valuePrefix = forBulkModal ? '' : `${filterName}: `;
		if (!selected) {
			return <div className={styles.emptyFilterContent}>{valuePrefix}All</div>;
		}

		return (
			<div className={styles.appliedFilterContent}>
				{valuePrefix}{selected.label}
			</div>
		);
	}, [filterName, forBulkModal, selected]);

	const containerStyle = React.useMemo(() => {
		let applicableStyle: string = '';
		if (forBulkModal) {
			applicableStyle = styles.bulkModalStyle;
		} else if (!!selected) {
			applicableStyle = styles.appliedFilter;
		} else {
			applicableStyle = styles.emptyFilter;
		}
		return [
			styles.additionalFilter,
			applicableStyle,
		].join(' ');
	}, [selected, forBulkModal]);

	const filterBy = React.useCallback((option: AdditionalFilterOption, rawInput: string) => {
		return option.label.toLowerCase().includes(rawInput.toLowerCase());
	}, []);

	return (
		<Dropdown<AdditionalFilterOption>
			autoOpen={!forBulkModal && autoOpen}
			containerClassName={containerStyle}
			defaultValue={defaultOption}
			dropdownId={`accqa__additional-filter-singleselect__${filterName}`}
			filterable={true}
			filterBy={filterBy}
			fixed={forBulkModal}
			forcePlaceholder={true}
			onClose={forBulkModal ? undefined : onRemoveFilter}
			onValueChange={onValueChange}
			options={allOptions}
			placeholder={placeholder}
			renderMenuItem={renderOption}
			valueKey="id"
			withCaret={true}
		/>
	);
};

export default React.memo(Singleselect);
